import { OBJECT_TYPE_LABELS } from '../approval/constants';
import type { ObjectType } from '../approval/types';
import type { NotificationData } from './types';

export interface FormattedNotification {
  title: string;
  description: string;
}

const objectTypeLabel = (data: NotificationData): string | null =>
  data.objectType ? OBJECT_TYPE_LABELS[data.objectType as ObjectType] ?? data.objectType : null;

export const formatNotification = (type: string, data: NotificationData): FormattedNotification => {
  switch (type) {
    case 'approval.user.pending':
      return {
        title: 'Nový uživatel čeká na schválení',
        description: [data.fullName, data.email].filter(Boolean).join(' · '),
      };
    case 'approval.company.pending':
      return {
        title: 'Nová firma čeká na schválení',
        description: [data.title, data.ico ? `IČO: ${data.ico}` : null].filter(Boolean).join(' · '),
      };
    case 'approval.employee.pending':
      return {
        title: 'Nový zaměstnanec čeká na schválení',
        description: [data.fullName, data.companyTitle].filter(Boolean).join(' · '),
      };
    case 'approval.request.approved':
      return {
        title: 'Žádost byla schválena',
        description: [objectTypeLabel(data), data.title].filter(Boolean).join(' · '),
      };
    case 'approval.request.rejected':
      return {
        title: 'Žádost byla zamítnuta',
        description: [objectTypeLabel(data), data.title, data.comment].filter(Boolean).join(' · '),
      };
    case 'approval.request.commented':
      return {
        title: 'Nový komentář ke schválení',
        description: [data.actorName, objectTypeLabel(data), data.comment]
          .filter(Boolean)
          .join(' · '),
      };
    case 'pdf.generation.done':
      return {
        title: 'PDF je připravené',
        description: data.name ?? '',
      };
    case 'pdf.generation.failed':
      return {
        title: 'Generování PDF selhalo',
        description: [data.name, data.reason].filter(Boolean).join(' · '),
      };
    default:
      return { title: 'Notifikace', description: '' };
  }
};

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
];

export const formatRelativeTime = (iso: string, now: number = Date.now()): string => {
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return '';

  const diffSeconds = Math.round((timestamp - now) / 1000);
  const absSeconds = Math.abs(diffSeconds);
  if (absSeconds < 45) return 'právě teď';

  const rtf = new Intl.RelativeTimeFormat('cs', { numeric: 'auto' });
  for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
    if (absSeconds >= secondsInUnit) {
      return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return 'právě teď';
};
